import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type {
    TimeGranularity} from "@@/shared/date-time/time-granularity";
import {
    addMs,
    convertTimeByGranularity,
    timeGranularityZodSchema,
} from "@@/shared/date-time/time-granularity";
import { timeRangeByDurationFactory } from "@@/shared/date-time/time-range";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import type {
    MinMaxRangeWithRequiredMin} from "@@/utilities/set-within-range";
import {
    minMaxWithRequiredMinZodSchema,
} from "@@/utilities/set-within-range";
import type { Except } from "type-fest";
import { z } from "zod";
import type { EntityState} from "../../entity-status";
import { entityStateZodSchema } from "../../entity-status";
import type { ProductId} from "../../products/product-id";
import { productIdZodSchema } from "../../products/product-id";
import type { SkuId} from "../../products/sku-id";
import { skuIdZodSchema } from "../../products/sku-id";
import type {
    DurationBaseLength} from "../bookable-base-length";
import {
    durationBaseLengthZodSchema,
} from "../bookable-base-length";
import type {
    BookableSettings,
    BookableSettings_V2} from "../bookable-settings";
import {
    bookableSettingsV2ZodSchema,
    defaultBookableSettings,
} from "../bookable-settings";
import type {
    ResourceMappings} from "../resource-references/resource-override";
import {
    resourceMappingsZodSchema,
} from "../resource-references/resource-override";
import type {
    BookableSessionId} from "./bookable-session-id";
import {
    bookableSessionIdFactory,
    bookableSessionIdZodSchema,
} from "./bookable-session-id";
import type {
    BookableSessionSeriesId} from "./bookable-session-series-id";
import {
    bookableSessionSeriesIdZodSchema,
} from "./bookable-session-series-id";
import type {
    SessionStatus} from "./bookable-session-status";
import {
    sessionStatusZodSchema,
} from "./bookable-session-status";

type BookableSession = {
    readonly _id: BookableSessionId;
    readonly _type: "BOOKABLE_SESSION";
    readonly _status: EntityState;
    readonly _createdAt: IsoAndUnixTimestamp;
    /**
     * Session starts at `start`, and ends after `durationMinMax.min`
     * @type {IsoAndUnixTimestamp}
     */
    readonly start: IsoAndUnixTimestamp;
    /**
     * Bookable can be booked with a duration between min and max in intervalls defined by `durationBaseLength`
     * @type {MinMaxRange}
     */
    readonly durationMinMax: MinMaxRangeWithRequiredMin;
    /**
     * Defines what unit `durationMinMax` and `durationBaseLength` is defined in
     * @type {TimeGranularity}
     */
    readonly durationGranularity: TimeGranularity;
    /**
     * When customer books the session and wants to extend time more than
     * the min value, the base length is how long an extension is required to be.
     *
     * E.g. A session can have minmax set to 2h to 8h and base length set to 30min \
     * When booking, customer can choose to book at minimum 2h, and prolong the booking in \
     * intervals of 30min until reaching the maximum of 8h.
     * @type {DurationBaseLength}
     */
    readonly durationBaseLength: DurationBaseLength;
    /**
     * When a session is created its often created at the same time a
     * number of other sessions on different times. \
     * All sessions created at the same time from the same user choices
     * get the same series id
     * @type {BookableSessionSeriesId}
     */
    readonly seriesId: BookableSessionSeriesId;
    /**
     * Parent bookable product id
     * @type {ProductId}
     */
    readonly productId: ProductId;
    /**
     * Parent bookable product sku id
     * @type {SkuId}
     */
    readonly skuId: SkuId;

    readonly status: SessionStatus;

    /**
     * Overrides/replaces (if set to a value) the same prop from parent bookable
     * @type {ResourceReference[]}
     */
    //readonly resourceReferences: ResourceReference[];
    readonly bookableSettings: BookableSettings_V2;

    readonly mappings: ResourceMappings;
};

type CreateBookableSessionParams = Except<
    BookableSession,
    "_id" | "_type" | "_createdAt" | "_status" | "bookableSettings"
    //  | "resourceReferences"
>;

/**
 * params._id defautls to new bookable session id \
 * params._status defaults to `ACTIVE`
 */
const createBookableSession = (
    params: CreateBookableSessionParams & {
        _id?: BookableSessionId;
        _status?: EntityState;
        bookableSettings?: BookableSettings_V2;
    },
): BookableSession => {
    const bookableSettings = params.bookableSettings ?? defaultBookableSettings;
    // const resourceReferences =
    //     params.resourceReferences ?? emptyArrayOf<ResourceReference>();
    return {
        ...params,
        _id: params._id || bookableSessionIdFactory(),
        _type: "BOOKABLE_SESSION",
        _createdAt: nowIsoAndUnix(),
        _status: params._status || "ACTIVE",
        //resourceReferences,
        bookableSettings,
    };
};

const bookableSessionZodSchema: z.ZodSchema<BookableSession> = z.object({
    _id: bookableSessionIdZodSchema,
    _type: z.literal("BOOKABLE_SESSION"),
    _status: entityStateZodSchema,
    _createdAt: isoAndUnixZodSchema,
    start: isoAndUnixZodSchema,
    durationGranularity: timeGranularityZodSchema,
    productId: productIdZodSchema,
    skuId: skuIdZodSchema,
    durationBaseLength: durationBaseLengthZodSchema,
    durationMinMax: minMaxWithRequiredMinZodSchema,
    seriesId: bookableSessionSeriesIdZodSchema,
    status: sessionStatusZodSchema,
    bookableSettings: bookableSettingsV2ZodSchema,
    mappings: resourceMappingsZodSchema,
});

const calculateBookableSessionDefaultTimeRange = (session: BookableSession) => {
    return timeRangeByDurationFactory(session.start, {
        value: session.durationMinMax.min,
        granularity: session.durationGranularity,
    });
};
const calculateBookableSessionDefaultTimeRangeWithPostTime = (
    session: BookableSession,
) => {
    const durationInMS = addMs(
        convertTimeByGranularity(
            session.durationMinMax.min,
            session.durationGranularity,
            "MILLISECOND",
        ),
        convertTimeByGranularity(
            session.bookableSettings.postTime,
            "MINUTE",
            "MILLISECOND",
        ),
    );
    return timeRangeByDurationFactory(session.start, {
        value: durationInMS,
        granularity: "MILLISECOND",
    });
};

const overrideBookableSettings = (
    session: BookableSession,
): BookableSession => {
    const setting: BookableSettings = {
        ...session.bookableSettings,
        advanceNotice: 0 as DurationInMinutes,
        allowBookingDuringSession: true,
    };
    return { ...session, bookableSettings: setting };
};

export {
    bookableSessionZodSchema,
    calculateBookableSessionDefaultTimeRange,
    calculateBookableSessionDefaultTimeRangeWithPostTime,
    createBookableSession,
    overrideBookableSettings,
};
export type { BookableSession };
